import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, getCurrencySymbol, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

interface DailyRow {
    date: string;
    grossSales: number;
    adSpend: number;
    shippingRevenue: number;
    transactionFees: number;
    discounts: number;
    returns: number;
    net: number;
}

export const DailyPLTableChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const rows = useMemo((): DailyRow[] => {
        if (!finance?.dailyBreakdown) return [];
        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                return {
                    date,
                    grossSales: metrics?.grossSales ?? 0,
                    adSpend: metrics?.adSpend ?? 0,
                    shippingRevenue: metrics?.shippingRevenue ?? 0,
                    transactionFees: metrics?.transactionFees ?? 0,
                    discounts: metrics?.discounts ?? 0,
                    returns: metrics?.returns ?? 0,
                    net: metrics?.net ?? 0,
                };
            });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => rows.length === 0, [rows]);

    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    const columns = [
        { key: 'date' as const, label: t('charts.date', 'Date') },
        { key: 'grossSales' as const, label: t('charts.grossSales', 'Gross Sales') },
        { key: 'adSpend' as const, label: t('charts.adSpend', 'Ad Spend') },
        { key: 'shippingRevenue' as const, label: t('charts.shipping', 'Shipping') },
        { key: 'transactionFees' as const, label: t('charts.fees', 'Fees') },
        { key: 'discounts' as const, label: t('charts.discounts', 'Discounts') },
        { key: 'returns' as const, label: t('charts.returns', 'Returns') },
        { key: 'net' as const, label: t('charts.netProfit', 'Net Profit') },
    ];

    return (
        <div className="h-full w-full overflow-auto text-primary">
            <table className="w-full min-w-[600px] text-[10px]">
                <thead>
                    <tr className="border-b border-subtle">
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-neutral-500 dark:text-neutral-400 whitespace-nowrap"
                            >
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => (
                        <tr
                            key={row.date}
                            className={idx % 2 === 0 ? 'bg-background-page' : 'bg-background-elevated'}
                        >
                            <td className="px-3 py-2 whitespace-nowrap">
                                {format(parseISO(row.date), 'MMM d, yyyy')}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                {formatCurrency(row.grossSales, currencySymbol)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                {formatCurrency(row.adSpend, currencySymbol)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                {formatCurrency(row.shippingRevenue, currencySymbol)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                {formatCurrency(row.transactionFees, currencySymbol)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                {formatCurrency(row.discounts, currencySymbol)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                {formatCurrency(row.returns, currencySymbol)}
                            </td>
                            <td
                                className="px-3 py-2 whitespace-nowrap font-semibold"
                                style={{ color: row.net >= 0 ? CHART_COLORS.revenue : CHART_COLORS.expense }}
                            >
                                {formatCurrency(row.net, currencySymbol)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
